<template>
  <v-menu>
    <template #activator="{ on: onMenu }">
      <v-tooltip left>
        <template #activator="{ on: onTooltip }">
          <v-icon v-on="{ ...onMenu, ...onTooltip }" :disabled="isLoading">mdi-file-download-outline</v-icon>
        </template>
        <span>Export</span>
      </v-tooltip>
    </template>
    <v-list>
      <v-list-item :disabled="isLoading">
        <v-list-item-title @click="handleExport('csv')">CSV</v-list-item-title>
      </v-list-item>
      <v-list-item :disabled="isLoading">
        <v-list-item-title @click="handleExport('excel')">Excel</v-list-item-title>
      </v-list-item>
      <v-list-item :disabled="isLoading">
        <v-list-item-title @click="handleExport('clipboard')">Clipboard</v-list-item-title>
      </v-list-item>
      <v-list-item v-if="showESNsToClipboard" :disabled="isLoading">
        <v-list-item-title @click="handleExport('clipboard-esn')">ESNs to Clipboard</v-list-item-title>
      </v-list-item>
    </v-list>
  </v-menu>
</template>

<script>
import XLSX from 'xlsx';
export default {
  data: () => ({
    isLoading: false,
  }),
  props: {
    csvData: Array,
    excelData: Array,
    clipboardData: String,
    sessionInfo: Object,
    emptyItems: String,
    clipboardDataOnlyESN: String,
    showESNsToClipboard: {
      type: Boolean,
      default: false
    },
    // New props for async data fetching
    exportDataFetcher: {
      type: Function,
      default: null
    },
    exportParams: {
      type: Object,
      default: () => ({})
    },
  },
  computed: {
    filename() {
      let timestamp = Math.floor(+new Date / 1000)
      return this.emptyItems.toLowerCase() +"_" + this.sessionInfo.username + "_" + this.sessionInfo.org_abbr + "_" + timestamp
    },
  },
  methods: {
    async handleExport(format) {
      this.isLoading = true;
      
      try {
        // Special handling for clipboard with async fetcher and Safari ClipboardItem
        // Must create ClipboardItem synchronously in click handler to preserve user gesture
        if ((format === 'clipboard' || format === 'clipboard-esn') && 
            this.exportDataFetcher && 
            typeof ClipboardItem !== 'undefined' && 
            navigator.clipboard && navigator.clipboard.write) {
          
          // Create the promise that will fetch and format the data
          const dataPromise = this.exportDataFetcher(format, this.exportParams);
          
          // Create blob promise that will resolve when data is ready
          const blobPromise = dataPromise.then(text => {
            return new Blob([text], { type: "text/plain" });
          });
          
          // Write to clipboard immediately (synchronously) with the promise
          await navigator.clipboard.write([new ClipboardItem({ "text/plain": blobPromise })]);
          this.$emit('success', this.emptyItems + " copied to clipboard.");
          this.isLoading = false;
          return;
        }
        
        // Standard flow for non-clipboard or non-async operations
        let exportData = null;
        
        // Check if we need to fetch data asynchronously
        if (this.exportDataFetcher) {
          exportData = await this.exportDataFetcher(format, this.exportParams);
        } else {
          // Use direct data props
          exportData = {
            csv: this.csvData,
            excel: this.excelData,
            clipboard: this.clipboardData,
            clipboardESN: this.clipboardDataOnlyESN
          };
        }
        
        // Perform the export based on format
        if (format === 'csv') {
          this.csvExport(exportData.csv || exportData);
        } else if (format === 'excel') {
          this.excelExport(exportData.excel || exportData);
        } else if (format === 'clipboard') {
          await this.clipboardExport(exportData.clipboard || exportData);
        } else if (format === 'clipboard-esn') {
          await this.clipboardExport(exportData.clipboardESN || this.clipboardDataOnlyESN);
        }
      } catch (error) {
        console.error('Export error:', error);
        this.$emit('error', 'Export failed. Please try again.');
      } finally {
        this.isLoading = false;
      }
    },
    
    csvExport(csvData) {
      if (csvData ===  null || csvData === undefined || csvData.length == 0) {
        this.$emit('error', "No " + this.emptyItems.toLowerCase() + " to export.");
      } else {
        let csvContent = "data:text/csv;charset=utf-8,";
        csvContent += [
          Object.keys(csvData[0]).join(","),
          ...csvData.map(item => Object.values(item).join(","))
        ]
          .join("\n")
          .replace(/(^\[)|(\]$)/gm, "");

        const data = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", data);
        link.setAttribute("download", this.filename + ".csv");
        link.click();
        this.$emit('success', this.emptyItems + " exported to CSV.");
      }
    },
    
    excelExport(excelData) {
      if (excelData ===  null || excelData === undefined || excelData.length == 0) {
        this.$emit('error', "No " + this.emptyItems.toLowerCase() + " to export.");
      } else {
        const data = XLSX.utils.json_to_sheet(excelData)
        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, data, 'data')
        XLSX.writeFile(wb,this.filename + ".xlsx" )
        this.$emit('success', this.emptyItems + " exported to Excel.");
      }
    },
    
    async clipboardExport(clipboardData) {
      // Safari-compatible clipboard implementation
      if (typeof ClipboardItem !== 'undefined' && navigator.clipboard.write) {
        try {
          const blobPromise = Promise.resolve(clipboardData).then(text => {
            return new Blob([text], { type: "text/plain" });
          });
          
          await navigator.clipboard.write([new ClipboardItem({ "text/plain": blobPromise })]);
          this.$emit('success', this.emptyItems + " copied to clipboard.");
        } catch (err) {
          console.error('Clipboard error:', err);
          this.$emit('error', this.emptyItems + " failed to copy to clipboard. Please try again.");
        }
      } else {
        // Fallback for other browsers
        await this.copyTextToClipboard(clipboardData);
      }
    },
    
    async copyTextToClipboard(text) {
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text);
          this.$emit('success', this.emptyItems + " copied to clipboard.");
        } else {
          // Fallback for older browsers
          let textArea = document.createElement("textarea");
          textArea.value = text;
          textArea.style.position = "fixed";
          textArea.style.top = "0";
          textArea.style.left = "0";
          textArea.style.width = "2em";
          textArea.style.height = "2em";
          textArea.style.padding = "0";
          textArea.style.border = "none";
          textArea.style.outline = "none";
          textArea.style.boxShadow = "none";
          textArea.style.background = "transparent";
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          
          try {
            const successful = document.execCommand('copy');
            if (successful) {
              this.$emit('success', this.emptyItems + " copied to clipboard.");
            } else {
              this.$emit('error', this.emptyItems + " failed to copy to clipboard. Please try again.");
            }
          } catch (err) {
            console.error('Fallback: Oops, unable to copy', err);
            this.$emit('error', this.emptyItems + " failed to copy to clipboard. Please try again.");
          }
          
          document.body.removeChild(textArea);
        }
      } catch (err) {
        console.error('Could not copy text: ', err);
        this.$emit('error', this.emptyItems + " failed to copy to clipboard. Please try again.");
      }
    },
  }
}
</script>


